<template>
    <fragment>
        <b-table :fields="fields" :items="items" :busy="tableBusy" :current-page="currentPage" hover no-border-collapse
            sort-by="" :sort-desc="true" striped stacked="lg" show-empty>
            <template #empty>
                <span>Não foram encontrados dados.</span>
            </template>
            <template #emptyfiltered>
                <span>Não foram encontrados dados.</span>
            </template>
            <template #table-busy>
                <div class="text-center text-dark">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="loading-text">Carregando...</strong>
                </div>
            </template>
            <template v-slot:cell(detalhesVencedores)="row">
                <b-link class="text-dark" @click="row.toggleDetails">
                    <font-awesome-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'" />
                </b-link>
            </template>
            <template #row-details="row">
                <editar-participantes-vencedores-itens-licitacao :hmlParcial="licitacao.FlHomologParcial"
                    @itemAtualizado="atualizarItemNaTabela(row.item)"
                    :itemComVencedores="row.item"></editar-participantes-vencedores-itens-licitacao>
            </template>
            <template #cell(DeItemMatServ)="data">
                <TextoLongo :texto="data.item.DeItemMatServ" :limite="35" class="w-100" />
            </template>
            <template #cell(FlFracassado)="data">
                <span>{{ data.item.FlFracassado }}</span>
                <seletor-fracassado-item :itemLicitacao="data.item"
                    @sitFracassoAtualizada="e => { atualizarVencedor(data.item, e); atualizarItemNaTabela(data.item) }"></seletor-fracassado-item>
            </template>
            <template #custom-foot>
                <b-tr class="font-weight-bold">
                    <b-td colspan="6" class="text-left">Totais</b-td>
                    <b-td class="text-right">
                        {{ licitacaoVlTotalEstimado ? formatMoeda(licitacaoVlTotalEstimado) : '' }}
                    </b-td>
                    <b-td></b-td>
                    <b-td class="text-right">
                        {{ licitacaoVlTotalLicitado ? formatMoeda(licitacaoVlTotalLicitado) : '' }}
                    </b-td>
                </b-tr>
            </template>
        </b-table>
        <table-pagination v-if="totalRows > 10" :total-rows="totalRows" :per-page="10" @pageChange="pageChange" />
    </fragment>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { UtilsTS } from '@/utilsts'
import { eventBus } from '@/main'

import ldiService from '@/services/licitacoes/ldiService'
import { TableField } from '@/types/table-field'

import { detalhesVencedores, fieldsItemVencedorLicitacao } from '@/types/licitacoes/ldi/fields-item-licitacao'
import { LicitacaoVencedorItem } from '@/types/licitacoes/ldi/licitacao-vencedor-item'

import SeletorFracassadoItem from '@/components/licitacoes/ldi/editar-ldi/SeletorFracassadoItem.vue'
import EditarParticipantesVencedoresItensLicitacao from './EditarParticipantesVencedoresItensLicitacao.vue'
import TablePagination from '@/components/atos-pessoal/TablePagination.vue'
import { ParticipanteLicitacao } from '@/types/licitacoes/ldi/participante-licitacao'
import { PropType } from 'vue'
import { Licitacao } from '@/types/licitacoes/ldi/licitacao'
import TextoLongo from '@/components/shared/TextoLongo.vue'

const CompProps = Vue.extend({
    name: 'EditarVencedoresItemTable',
    components: { EditarParticipantesVencedoresItensLicitacao, SeletorFracassadoItem, TablePagination, TextoLongo },
    props: {
        licitacao: {
            type: Object as PropType<Licitacao>,
            required: true
        },
        textoFiltro: {
            type: String,
            default: ''
        }
    }
})

@Component
export default class EditarVencedoresItemTable extends mixins(UtilsTS, CompProps) {
    fields: TableField[] = [detalhesVencedores, ...fieldsItemVencedorLicitacao]
    items: LicitacaoVencedorItem[] = []
    participantes: ParticipanteLicitacao[] = []

    //table-related
    currentPage = 1
    pageSize = 10
    totalRows = 0
    tableBusy = false
    licitacaoVlTotalLicitado: number | null = null
    licitacaoVlTotalEstimado: number | null = null

    async mounted(): Promise<void> {
        await this.buscarParticipantes()
        await this.loadData()
    }
    async pageChange(value: number): Promise<void> {
        this.currentPage = value;
        await this.loadData();
    }
    async loadData(): Promise<void> {
        try {
            this.tableBusy = true
            const response = await ldiService.buscarItensEdicaoVencedores(this.licitacao.IdLicitacao, this.currentPage, this.pageSize, this.textoFiltro)
            this.items = response.itensComVencedores
            this.licitacaoVlTotalLicitado = response.licitacaoVlTotalLicitado;
            this.licitacaoVlTotalEstimado = response.licitacaoVlTotalEstimado;
            this.totalRows = response.totalRows
        } catch (e) {
            console.error(e)
            this.$toast.error('Falha ao buscar vencedores. ' + e)
            this.limpar()
        } finally {
            this.tableBusy = false
        }
    }

    limpar(): void {
        this.items = []
        this.totalRows = 0
        this.licitacaoVlTotalLicitado = null
        this.licitacaoVlTotalEstimado = null
    }

    async buscarParticipantes(): Promise<void> {
        try {
            this.tableBusy = true
            const response = await ldiService.buscarParticipantes(this.licitacao.IdLicitacao)
            if (response) {
                // console.log('peguei os participantes', response)
                this.participantes = response.participantes.map(p => {
                    const item = Object.assign(new ParticipanteLicitacao(), p)
                    return item
                });
            } else {
                this.$toast.error('Erro ao consultar os participantes da licitação.');
            }
        } catch (e) {
            console.error(e)
            this.$toast.error('Falha ao buscar participantes. ' + e)
        } finally {
            this.tableBusy = false
        }
    }

    async ativarModoEdicaoVencedorItem(item: LicitacaoVencedorItem): Promise<void> {
        const meuItem = this.items.find(lo => lo.IdItemLicitacao === item.IdItemLicitacao)
        if (meuItem) {
            this.fecharTodasRows()
            meuItem.modoEdicao = true
            meuItem._rowVariant = 'active'
            meuItem._showDetails = true
        }
    }

    async desativarModoEdicaoVencedorItem(item: LicitacaoVencedorItem): Promise<void> {
        const meuItem = this.items.find(lo => lo.IdItemLicitacao === item.IdItemLicitacao)
        if (meuItem) {
            meuItem.modoEdicao = false
            meuItem._rowVariant = ''
        }
    }

    async atualizarVencedor(lvl: LicitacaoVencedorItem, lvlAtualizado: LicitacaoVencedorItem): Promise<void> {
        try {
            const novoLvl = Object.assign(lvl, lvlAtualizado)
            const index = this.items.indexOf(lvl)
            this.items.splice(index, 1, novoLvl)
            this.$emit('input', this.items);
            eventBus.$emit('atualizar-status-licitacao')
            eventBus.$emit('atualizar-homologacao-licitacao')
            this.$toast.success(`O item ${novoLvl.DeItem} foi atualizado!`)
        } catch (e: any) {
            this.tratarError(e)
        }
    }

    fecharTodasRows() {
        this.items.forEach(item => item._showDetails = false)
        this.$emit('input', this.items);
    }

    async atualizarItemNaTabela(item: LicitacaoVencedorItem, buscarNaAPI = true) {
        const meuItem = this.items.find(l => l.IdItemLicitacao === item.IdItemLicitacao)
        if (meuItem) {
            try {
                meuItem.loading = true
                const index = this.items.indexOf(meuItem)
                if (buscarNaAPI) {
                    item = await ldiService.buscarItem(item.IdItemLicitacao)
                    item = Object.assign(new LicitacaoVencedorItem(), item)
                    item.tratarDadosDaAPI()
                }

                //console.log("item", item)
                this.items.splice(index, 1, item)
                eventBus.$emit('atualizar-status-licitacao')
                this.$emit('input', this.items);
            } catch (e: any) {
                this.tratarError(e)
            }
        }
    }
}
</script>